import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type ReportSummary } from '../lib/api';
import { EmptyState, Money, Spinner, StatusPill } from '../components/ui';

/** Cards on a phone; a table from `lg:` up. Same data, two densities. */
export function MyReportsPage() {
  const [reports, setReports] = useState<ReportSummary[] | null>(null);

  useEffect(() => {
    api.get<{ reports: ReportSummary[] }>('/reports').then((d) => setReports(d.reports)).catch(() => setReports([]));
  }, []);

  if (!reports) return <Spinner label="Loading your reports" />;

  return (
    <div className="pb-24">
      <header className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold">My reports</h1>
        <Link to="/reports/new" className="btn-primary hidden lg:inline-flex">New report</Link>
      </header>

      {reports.length === 0 ? (
        <EmptyState
          title="No expense reports yet"
          hint="Start a monthly report for routine spend, or a travel report for a trip."
          action={<Link to="/reports/new" className="btn-primary">Create your first report</Link>}
        />
      ) : (
        <>
          {/* Mobile: cards */}
          <ul className="space-y-3 lg:hidden">
            {reports.map((r) => (
              <li key={r.id}>
                <Link to={`/reports/${r.id}`} className="card block p-4 active:bg-slate-50">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{r.title}</p>
                      <p className="mt-0.5 text-sm text-slate-500">
                        {r.type === 'travel' ? r.tripDestination : r.periodMonth?.slice(0, 7)}
                        {' · '}
                        {r.lineCount} item{r.lineCount === 1 ? '' : 's'}
                      </p>
                    </div>
                    <Money value={r.submittedTotalAmount ?? r.currentTotal} className="font-semibold" />
                  </div>
                  <div className="mt-2"><StatusPill status={r.status} /></div>
                </Link>
              </li>
            ))}
          </ul>

          {/* Desktop: table */}
          <div className="card hidden overflow-hidden lg:block">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr>
                  <th className="px-4 py-2 font-medium">Title</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Period / destination</th>
                  <th className="px-4 py-2 font-medium">Items</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {reports.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <Link to={`/reports/${r.id}`} className="font-medium text-brand hover:underline">{r.title}</Link>
                    </td>
                    <td className="px-4 py-2 capitalize">{r.type}</td>
                    <td className="px-4 py-2">{r.type === 'travel' ? r.tripDestination : r.periodMonth?.slice(0, 7)}</td>
                    <td className="px-4 py-2">{r.lineCount}</td>
                    <td className="px-4 py-2"><StatusPill status={r.status} /></td>
                    <td className="px-4 py-2 text-right"><Money value={r.submittedTotalAmount ?? r.currentTotal} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Thumb-zone FAB, phone only. */}
      <Link
        to="/reports/new"
        aria-label="New report"
        className="fixed right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-brand text-3xl leading-none text-white shadow-lg lg:hidden"
        style={{ bottom: 'calc(1.25rem + env(safe-area-inset-bottom))' }}
      >
        +
      </Link>
    </div>
  );
}
